import { usePricingTier } from '@/hooks/use-pricing-tier'
import type { PricingLimits } from '@/hooks/use-pricing-tier'

/* ============================================================
   FeatureGate — Declarative component for gating UI
   ============================================================ */

interface FeatureGateProps {
  feature: keyof PricingLimits
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function FeatureGate({ feature, children, fallback }: FeatureGateProps) {
  const { hasFeature, requiredTier, isLoading } = usePricingTier()

  // Prevent flash of locked state while plan loads (tier defaults to trial during load)
  if (isLoading) return null

  if (hasFeature(feature)) {
    return <>{children}</>
  }

  if (fallback) {
    return <>{fallback}</>
  }

  // Default upgrade prompt
  const required = requiredTier(feature)
  return (
    <div className="mac-glass rounded-xl p-6 text-center border border-white/[0.07]">
      <p className="text-lg mb-2">🔒</p>
      <p className="text-sm text-text-primary font-medium">
        {String(feature)
          .replace(/([A-Z])/g, ' $1')
          .trim()}{' '}
        requires the <span className="text-origin-accent capitalize">{required}</span> plan
      </p>
      <button className="mt-3 px-4 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-xs font-medium hover:opacity-90 transition-opacity">
        Upgrade
      </button>
    </div>
  )
}
